import React, { useState, useEffect } from 'react';
import {
  FaTimes,
  FaChartLine,
  FaUsers,
  FaMoneyBillWave,
  FaQrcode,
  FaStar,
} from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../context/AuthContext';

function BusinessAuditModal({ business, onClose }) {
  const { getAdminToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [checkinHistory, setCheckinHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (business) {
      fetchAuditData();
    }
  }, [business]);

  const fetchAuditData = async () => {
    try {
      // 1. Ümumi statistika
      const { count: totalCheckins } = await supabase
        .from('check_ins')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', business.id);

      const { count: uniqueUsers } = await supabase
        .from('check_ins')
        .select('user_id', { count: 'exact', head: true })
        .eq('business_id', business.id);

      const { count: qrScans } = await supabase
        .from('check_ins')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', business.id)
        .not('qr_code', 'is', null);

      // 2. Revenue
      const { data: revRes } = await supabase.rpc('admin_get_revenue_logs_full', {
        p_admin_token: getAdminToken(), p_business_id: business.id,
      });
      const revenueData = revRes?.success ? revRes.rows : [];

      const totalRevenue = revenueData?.reduce(
        (sum, log) => sum + parseFloat(log.amount || 0),
        0
      ) || 0;

      // 3. Average per day
      const businessAge = Math.floor(
        (new Date() - new Date(business.created_at)) / (1000 * 60 * 60 * 24)
      );
      const avgCheckinsPerDay = businessAge > 0 ? (totalCheckins / businessAge).toFixed(1) : 0;

      setStats({
        totalCheckins,
        uniqueUsers,
        qrScans,
        totalRevenue,
        businessAge,
        avgCheckinsPerDay,
      });

      // 4. Top Users (bu business-ə ən çox check-in edən)
      const { data: checkinsData } = await supabase
        .from('check_ins')
        .select('user_id, users(full_name, username, avatar_url)')
        .eq('business_id', business.id);

      // User-ə görə qruplaşdır
      const userCheckinsMap = {};
      checkinsData?.forEach((checkin) => {
        const userId = checkin.user_id;
        if (!userCheckinsMap[userId]) {
          userCheckinsMap[userId] = {
            userId,
            userName: checkin.users?.full_name || 'Unknown',
            username: checkin.users?.username || 'unknown',
            avatarUrl: checkin.users?.avatar_url,
            count: 0,
          };
        }
        userCheckinsMap[userId].count++;
      });

      const topUsersList = Object.values(userCheckinsMap)
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setTopUsers(topUsersList);

      // 5. Check-in History (son 30 gün)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: historyData } = await supabase
        .from('check_ins')
        .select('created_at')
        .eq('business_id', business.id)
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at', { ascending: true });

      // Günə görə qruplaşdır
      const dailyCheckins = {};
      historyData?.forEach((checkin) => {
        const date = new Date(checkin.created_at).toLocaleDateString('az-AZ');
        dailyCheckins[date] = (dailyCheckins[date] || 0) + 1;
      });

      const historyChart = Object.entries(dailyCheckins).map(([date, count]) => ({
        date,
        checkins: count,
      }));

      setCheckinHistory(historyChart);
    } catch (error) {
      console.error('Error fetching audit data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!business) return null;

  return (
    <>
      <div style={styles.overlay} onClick={onClose}></div>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>🏢 {business.name} - Tam Audit</h2>
            <p style={styles.subtitle}>Detallı statistika və performans</p>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>
            <FaTimes size={24} />
          </button>
        </div>

        {loading ? (
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <p>Yüklənir...</p>
          </div>
        ) : (
          <div style={styles.content}>
            {/* Stats Grid */}
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <FaUsers size={32} color="#2196F3" />
                <div>
                  <p style={styles.statLabel}>Ümumi Check-ins</p>
                  <h3 style={styles.statValue}>{stats?.totalCheckins || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <FaStar size={32} color="#FF9800" />
                <div>
                  <p style={styles.statLabel}>Unikal İstifadəçilər</p>
                  <h3 style={styles.statValue}>{stats?.uniqueUsers || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <FaQrcode size={32} color="#9C27B0" />
                <div>
                  <p style={styles.statLabel}>QR Scan-lar</p>
                  <h3 style={styles.statValue}>{stats?.qrScans || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <FaMoneyBillWave size={32} color="#4CAF50" />
                <div>
                  <p style={styles.statLabel}>Gətirdiyi Gəlir</p>
                  <h3 style={styles.statValue}>{stats?.totalRevenue?.toFixed(2) || 0} ₼</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <FaChartLine size={32} color="#F44336" />
                <div>
                  <p style={styles.statLabel}>Orta Günlük Check-in</p>
                  <h3 style={styles.statValue}>{stats?.avgCheckinsPerDay || 0}</h3>
                </div>
              </div>

              <div style={styles.statCard}>
                <div style={styles.ageIcon}>🎂</div>
                <div>
                  <p style={styles.statLabel}>Business Yaşı</p>
                  <h3 style={styles.statValue}>{stats?.businessAge || 0} gün</h3>
                </div>
              </div>
            </div>

            {/* Chart */}
            <div style={styles.chartSection}>
              <h3 style={styles.sectionTitle}>📈 Check-in Tarixi (Son 30 gün)</h3>
              {checkinHistory.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={checkinHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#999" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#999" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '10px',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="checkins"
                      stroke="#2196F3"
                      strokeWidth={3}
                      dot={{ fill: '#2196F3', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p style={styles.noData}>Son 30 gündə check-in yoxdur</p>
              )}
            </div>

            {/* Top Users */}
            <div style={styles.topSection}>
              <h3 style={styles.sectionTitle}>🏆 TOP 5 Loyal Müştərilər</h3>
              {topUsers.length > 0 ? (
                <div style={styles.topList}>
                  {topUsers.map((user, index) => (
                    <div key={user.userId} style={styles.topItem}>
                      <div style={styles.topRank}>#{index + 1}</div>
                      <img
                        src={
                          user.avatarUrl ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            user.userName
                          )}&background=FF6B35&color=fff&size=50`
                        }
                        alt={user.userName}
                        style={styles.topAvatar}
                      />
                      <div style={styles.topInfo}>
                        <p style={styles.topName}>{user.userName}</p>
                        <p style={styles.topUsername}>@{user.username}</p>
                      </div>
                      <div style={styles.topCount}>
                        <span style={styles.topCountValue}>{user.count}</span>
                        <span style={styles.topCountLabel}>check-in</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={styles.noData}>Hələ check-in yoxdur</p>
              )}
            </div>

            {/* Performance Badge */}
            <div style={styles.performanceBadge}>
              <h4 style={styles.badgeTitle}>📊 Performans Qiymətləndirməsi</h4>
              <div style={styles.badgeContent}>
                {stats?.totalCheckins >= 100 && (
                  <span style={styles.badge}>🏆 100+ Check-in</span>
                )}
                {stats?.uniqueUsers >= 50 && (
                  <span style={styles.badge}>👥 50+ Müştəri</span>
                )}
                {stats?.avgCheckinsPerDay >= 5 && (
                  <span style={styles.badge}>🔥 Aktiv Business</span>
                )}
                {stats?.totalRevenue >= 1000 && (
                  <span style={styles.badge}>💰 Top Earner</span>
                )}
                {(!stats?.totalCheckins || stats.totalCheckins < 10) && (
                  <span style={styles.badgeWarning}>⚠️ Daha çox aktivlik lazımdır</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 2000,
  },
  modal: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '95%',
    maxWidth: '1000px',
    maxHeight: '90vh',
    overflowY: 'auto',
    zIndex: 2001,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    padding: '30px',
    borderBottom: '2px solid #F0F0F0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 1,
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  closeBtn: {
    backgroundColor: '#F44336',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  content: {
    padding: '30px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '20px',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
  },
  statLabel: {
    fontSize: '12px',
    color: '#999',
    marginBottom: '5px',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A2E',
  },
  ageIcon: {
    fontSize: '32px',
  },
  chartSection: {
    marginBottom: '30px',
    padding: '25px',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '20px',
  },
  topSection: {
    marginBottom: '30px',
    padding: '25px',
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
  },
  topList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  topItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  },
  topRank: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#FF6B35',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  topAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
  },
  topInfo: {
    flex: 1,
  },
  topName: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '3px',
  },
  topUsername: {
    fontSize: '13px',
    color: '#999',
  },
  topCount: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 15px',
    backgroundColor: '#E8F5E9',
    borderRadius: '8px',
  },
  topCountValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#2E7D32',
  },
  topCountLabel: {
    fontSize: '11px',
    color: '#666',
  },
  performanceBadge: {
    padding: '25px',
    backgroundColor: '#FFF3E0',
    borderRadius: '12px',
    borderLeft: '5px solid #FF9800',
  },
  badgeTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '15px',
  },
  badgeContent: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  badge: {
    padding: '8px 15px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
  },
  badgeWarning: {
    padding: '8px 15px',
    backgroundColor: '#FF9800',
    color: '#fff',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
  },
  noData: {
    textAlign: 'center',
    color: '#999',
    padding: '40px',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '60px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

export default BusinessAuditModal;